import React, { FC, useCallback } from 'react';
import { t } from '@lingui/macro';

import { Box, Button, Flex } from '@rover/kibble/core';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import type { ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets } from '@rover/rsdk/src/apiClient/latest/model/apiFrontendConfigurationRetrieve200SettingsDogSizeBuckets';
import type { HomepageSearchFilters } from '@rover/types/src/HomepageSearchFilters';
import { Pet } from '@rover/types/src/Pet';
import type { ServiceTypeOption } from '@rover/types/src/ServiceType';
import { ServiceType, ServiceTypeSlug } from '@rover/types/src/ServiceType';

import { HomepageCtaClickEvent } from '../../utilities/analyticsEvents';

import HomePageSearchBoxLocationInput from './connectors/LocationInputConnector';
import ServiceTypeConnector from './connectors/ServiceTypeConnector';
import type { UpdateFiltersFunc } from './UpdateFiltersFunc';

export interface Props {
  dateRangePickerLabel: string;
  endDatePlaceholder?: string;
  filters: HomepageSearchFilters;
  language: string;
  pets: Pet[];
  petSizeBuckets: ApiFrontendConfigurationRetrieve200SettingsDogSizeBuckets;
  serviceType: ServiceType | undefined;
  serviceTypeOptions: ServiceTypeOption[];
  startDatePlaceholder?: string;
  submit: () => void;
  submitTraining: () => void;
  updateFilters: UpdateFiltersFunc;
  weightUnit: string;
  className?: string;
}

const SearchBox: FC<Props> = (props: Props) => {
  const { filters, submit, submitTraining, serviceTypeOptions, updateFilters } = props;

  const { setScreenReaderAnnouncement } = useA11yContext();

  const isTraining = filters.serviceTypeSlug === ServiceTypeSlug.DOG_TRAINING;

  const handleSubmit = useCallback(() => {
    const homepageCtaClickEvent = new HomepageCtaClickEvent();
    homepageCtaClickEvent.fireCrossPageEvent();
    updateFilters({ spacesRequired: 1 });
    setScreenReaderAnnouncement(t`Form submitted. Navigating to Search page.`);
    if (isTraining) {
      submitTraining();
    } else {
      submit();
    }
  }, [isTraining, setScreenReaderAnnouncement, submit, submitTraining, updateFilters]);

  const areServicesLoaded = serviceTypeOptions?.length > 0;

  return (
    <Box
      className={props.className}
      background="primary"
      width={[768, 768, 850]}
      data-qa-id="homepage-search-box-container"
      data-testid="homepage-search-box-container"
      borderRadius="secondary"
    >
      <Box
        height="100%"
        p={['2x', '2x', '4x']}
        mt="0x"
        minHeight={areServicesLoaded ? undefined : '314px'}
      >
        {areServicesLoaded && (
          <>
            <ServiceTypeConnector {...props} />
            <Box>
              <Flex alignItems="flex-end" justifyContent="space-between" mb="3x">
                <Box flex="1" sx={{ maxWidth: '300px' }} mr="3x">
                  <HomePageSearchBoxLocationInput {...props} />
                </Box>
                <Box flex="1" sx={{ maxWidth: '250px' }}>
                  <Button
                    data-testid="search-box-submit"
                    size="default"
                    fullWidth
                    variant="primary"
                    onPress={handleSubmit}
                  >
                    {t`Search`}
                  </Button>
                </Box>
              </Flex>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SearchBox;
